
import { FindByIdMyArqanaHorseRegistrationDto } from '@/api/auth/myHorseRegistration'
import { documentsRule } from '@/views/registration/documents/rules'
import { formatISO } from 'date-fns'
import Vue, { PropType } from 'vue'
import add from 'date-fns/esm/add'
import parseISO from 'date-fns/fp/parseISO/index'

export default Vue.extend({
  props: {
    registration: {
      type: Object as PropType<FindByIdMyArqanaHorseRegistrationDto>,
    },
    pedigreeOpen: {
      type: Boolean,
      default: undefined,
    },
    documentOpen: {
      type: Boolean,
      default: undefined,
    },
    dense: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    isStepComplete(step: number): boolean {
      switch (step) {
        case 1:
          return true
        case 2:
          return this.registration.hasResponse
        case 3:
          return this.isPedigreeOpened
        case 4:
          return this.isPedigreeValidated
        case 5:
          return this.isMandatSigned
        case 6:
          return this.allMandatoryDocumentsUploaded
        case 7:
          return this.allMandatoryDocumentsOK
        default:
          return false
      }
    },
    isStepOpen(step: number): boolean {
      switch (step) {
        case 4:
          return this.isPedigreeOpened
        case 5:
          return this.isMandatOpen
        case 6:
          return this.isDocumentOpened
        case 7:
          return this.allMandatoryDocumentsUploaded
        default:
          return false
      }
    },
    getStepIcon(step: number): string {
      if (step === 2 && this.registration.state === 'REFUSED') {
        return 'mdi-close'
      }
      return 'mdi-check'
    },
    getStepColor(step: number): string {
      if (step === 2 && this.registration.state === 'REFUSED') {
        return 'red'
      }
      return this.isStepComplete(step) ? 'success' : 'primary'
    },
    shouldDisplayStep(step: number): boolean {
      if (this.registration.state === 'REFUSED' && step > 2) {
        return false
      }
      return true
    },
    shouldDisplayDivider(step: number, totalSteps: number): boolean {
      if (this.registration.state === 'REFUSED' && step === 2) {
        return false
      }
      return step < totalSteps
    },
  },
  computed: {
    allMandatoryDocumentsOK(): boolean {
      const rules = documentsRule[this.registration.type]
      if (rules) {
        const entries = Object.values(rules)
        return Object.keys(rules)
          .map((key, i) => ({ type: key, rule: entries[i] }))
          .filter(e => e.rule === 'COMPULSORY' || e.rule === 'COMPULSORY_IF_COVERED' && (this.registration.protrusionFormula === 'DEFAULT' || this.registration.protrusionFormula === 'FIRST'))
          .map(e => e.type)
          .every(e => this.registration.documents.find(x => x.type === e)?.status === 'ACCEPTED')
      }
      return true
    },
    allMandatoryDocumentsUploaded(): boolean {
      const rules = documentsRule[this.registration.type]
      if (rules) {
        const entries = Object.values(rules)
        return Object.keys(rules)
          .map((key, i) => ({ type: key, rule: entries[i] }))
          .filter(e => e.rule === 'COMPULSORY' || e.rule === 'COMPULSORY_IF_COVERED' && (this.registration.protrusionFormula === 'DEFAULT' || this.registration.protrusionFormula === 'FIRST'))
          .map(e => e.type)
          .every(e => ['ACCEPTED', 'WAITING'].includes(this.registration.documents.find(x => x.type === e)?.status ?? ''))
      }
      return true
    },
    isDocumentOpened(): boolean {
      if (this.documentOpen !== undefined) {
        return this.documentOpen
      }
      if (!['VISITED', 'COMPLETED'].includes(this.registration.state)) {
        return false
      }
      const dateNow = formatISO(new Date(), { representation: 'date' })
      const date30Offset = formatISO(add(parseISO(this.registration?.technicalSale.startDate), { days: -30 }), { representation: 'date' })
      return dateNow < this.registration?.exposingDay && dateNow >= date30Offset
    },
    isPedigreeOpened(): boolean {
      if (!['VISITED', 'COMPLETED'].includes(this.registration.state)) {
        return false
      }
      if (this.pedigreeOpen !== undefined) {
        return this.pedigreeOpen
      }
      const dateNow = formatISO(new Date(), { representation: 'date' })
      return this.registration.pedigreeAvailable && dateNow >= this.registration.technicalSale.correctionsStart && this.registration?.pedigreeAvailable && this.registration?.pedigreeUploaded
    },
    isPedigreeValidated(): boolean {
      if (!['VISITED', 'COMPLETED'].includes(this.registration.state)) {
        return false
      }
      const dateNow = formatISO(new Date(), { representation: 'date' })
      return this.isPedigreeOpened && (this.registration?.pedigreeValidated || dateNow > this.registration.technicalSale.correctionsEnd)
    },
    isMandatOpen(): boolean {
      return this.registration.availableMandat
    },
    isMandatSigned(): boolean {
      return this.registration.signedMandat
    },
  },
})
